/**
 * EntitlementEditor Component
 * Modal for editing user manual entitlements with multi-select interface
 * Allows administrators to grant or revoke MANUAL entitlements only (independently of
 * subscriptions). Entitlements that are normally delivered via a paid subscription bundle
 * are flagged so the admin knows a manual grant here is a comp/override.
 *
 * Saving sends only what the admin toggled, one grant at a time (#2029): see
 * `lib/manualEntitlementChanges.ts` for the rules. The parent mounts this only
 * while it is open, one instance per open, and hands it a snapshot fetched at
 * open. The snapshot is read once, in the `useState` initializer, and later
 * prop changes are ignored, so a background refetch can never wipe the admin's
 * clicks or move the baseline the diff is taken against.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { type UserProfile } from '../../lib/types/user';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FormButton } from '../form/FormButton';
import { UserAvatar } from '../ui/UserAvatar';
import { useAdminEntitlementsList } from '../../hooks/useAdminEntitlementsList';
import type { EntitlementDefinition } from '../../hooks/useAdminEntitlementsList';
import {
  diffManualEntitlements,
  formatManualEntitlementReport,
  formatOp,
  isCleanManualEntitlementBatch,
  ManualEntitlementSaveError,
  type ManualEntitlementBatch,
  type ManualEntitlementOp,
} from '../../lib/manualEntitlementChanges';

/** What the editor asks its parent to run: a Save, or a Retry of `pending`. */
export interface ManualEntitlementSaveRequest {
  /** In order: adds, then removes. On a Retry, exactly the pending list. */
  ops: ManualEntitlementOp[];
  /** The trimmed reason captured at Save; a Retry reuses it. */
  reason: string;
  /** Called before each call, 1-based. */
  onProgress: (current: number, total: number) => void;
  /** The batch so far, on a Retry: it carries the sticky marks and start time. */
  previous?: ManualEntitlementBatch;
  /** Aborted when the editor unmounts: no further call is issued after it. */
  signal?: AbortSignal;
}

export interface EntitlementEditorProps {
  user: UserProfile;
  onClose: () => void;
  /**
   * Runs the ops. Resolves with the batch, or rejects with
   * `ManualEntitlementSaveError` at the first failed call.
   */
  onSave: (
    request: ManualEntitlementSaveRequest
  ) => Promise<ManualEntitlementBatch>;
  /** The manual ids held at open, from a fresh fetch. Read once, at mount. */
  snapshot: readonly string[];
}

/**
 * - `editing`: the list is live and Save is available;
 * - `saving`: a batch, or a Retry, is running;
 * - `pending`: a call failed and ops remain: Retry / Continue, or Discard;
 * - `final`: the batch is over but did not end cleanly: the report, Close only.
 *
 * Once the editor leaves `editing` it never returns to it. An editable list on
 * a baseline the batch has already moved away from would diff wrongly;
 * reopening fetches afresh instead.
 */
type Phase = 'editing' | 'saving' | 'pending' | 'final';

interface EntitlementCardProps {
  entitlement: EntitlementDefinition;
  isSelected: boolean;
  onToggle: (entitlementId: string) => void;
  disabled?: boolean;
  isSubscriptionBundled?: boolean;
}

function EntitlementCard({
  entitlement,
  isSelected,
  onToggle,
  disabled = false,
  isSubscriptionBundled = false,
}: EntitlementCardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(entitlement.id);
    }
  };

  return (
    <div
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
        isSelected
          ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={() => !disabled && onToggle(entitlement.id)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {isSelected ? (
            <svg
              className="w-6 h-6 text-sky-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {entitlement.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {entitlement.description}
          </p>
          {isSubscriptionBundled && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
              <svg
                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                Paid subscription feature — granting here is a manual
                comp/override
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function EntitlementEditor({
  user,
  onClose,
  onSave,
  snapshot,
}: EntitlementEditorProps) {
  // Fetch available entitlements from API
  const {
    data: entitlementData,
    isLoading: entitlementsLoading,
    error: entitlementsError,
    isError: isEntitlementsError,
  } = useAdminEntitlementsList();

  const availableEntitlements = useMemo(
    () => entitlementData?.entitlements ?? [],
    [entitlementData?.entitlements]
  );

  const bundles = entitlementData?.bundles ?? [];
  const subscriptionEntitlementIds = useMemo(
    () => new Set(bundles.flatMap(b => b.entitlements)),
    [bundles]
  );

  const catalogueIds = useMemo(
    () => new Set(availableEntitlements.map(e => e.id)),
    [availableEntitlements]
  );

  // The frozen pair. Both sides come from the one snapshot, taken here, once;
  // `snapshot` is deliberately never read again.
  const [{ baseline, selection }, setFrozen] = useState(() => ({
    baseline: [...snapshot],
    selection: [...snapshot],
  }));
  const setSelection = (update: (prev: string[]) => string[]) =>
    setFrozen(prev => ({ ...prev, selection: update(prev.selection) }));

  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState<Phase>('editing');
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [batch, setBatch] = useState<ManualEntitlementBatch | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  // The confirmation is opened by the X, which it then disables, so focus
  // would drop to <body>. Move it to "Keep open", the safe choice.
  const confirmPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (confirmingDiscard) {
      confirmPanelRef.current?.querySelector('button')?.focus();
    }
  }, [confirmingDiscard]);
  // The reason captured at Save. A Retry replays with it, not with the field.
  const savedReason = useRef('');

  // Aborted on unmount, so a running batch stops after its in-flight call.
  // Created in the effect rather than the ref initializer: StrictMode's
  // mount, unmount, mount would otherwise abort the only controller for good.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => controller.abort();
  }, []);

  const isSaving = phase === 'saving';
  const isLocked = phase !== 'editing';

  // Warn before navigating away or closing the tab while a batch is running
  // or still has ops to send.
  const guardUnload = phase === 'saving' || phase === 'pending';
  useEffect(() => {
    if (!guardUnload) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Some browsers only prompt when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [guardUnload]);

  // Escape closes only before a batch has started. After that, leaving goes
  // through Discard or Close, and their confirmation.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'editing') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [phase, onClose]);

  // Check if changes have been made
  const hasChanges = useMemo(
    () => diffManualEntitlements(baseline, selection).length > 0,
    [baseline, selection]
  );

  // Check if reason is valid (not empty and meets length requirements)
  const isReasonValid = useMemo(() => {
    const trimmedReason = reason.trim();
    return trimmedReason.length >= 3 && trimmedReason.length <= 500;
  }, [reason]);

  // The counter and the bulk buttons see catalogue ids only. A held id with
  // no catalogue entry (a retired one, #1994) has no checkbox: it stays in the
  // selection, untouched, and so is never sent.
  const selectedCatalogueCount = selection.filter(id =>
    catalogueIds.has(id)
  ).length;
  const hiddenHeldCount = selection.length - selectedCatalogueCount;
  const allCatalogueSelected =
    availableEntitlements.length > 0 &&
    availableEntitlements.every(e => selection.includes(e.id));
  // Disabled when the catalogue failed: a click on an empty catalogue must not
  // be able to remove every grant.
  const bulkDisabled =
    isLocked ||
    entitlementsLoading ||
    isEntitlementsError ||
    availableEntitlements.length === 0;

  const handleToggleEntitlement = (entitlementId: string) => {
    if (isLocked) return;
    setSelection(prev =>
      prev.includes(entitlementId)
        ? prev.filter(id => id !== entitlementId)
        : [...prev, entitlementId]
    );
  };

  const handleSelectAll = () => {
    if (bulkDisabled) return;
    setSelection(prev => {
      const hidden = prev.filter(id => !catalogueIds.has(id));
      return allCatalogueSelected
        ? hidden
        : [...hidden, ...availableEntitlements.map(e => e.id)];
    });
  };

  const run = async (
    ops: ManualEntitlementOp[],
    previous: ManualEntitlementBatch | undefined
  ) => {
    setPhase('saving');
    setConfirmingDiscard(false);
    setProgress(null);
    const signal = abortRef.current?.signal;
    try {
      const result = await onSave({
        ops,
        reason: savedReason.current,
        onProgress: (current, total) => setProgress({ current, total }),
        previous,
        signal,
      });
      if (signal?.aborted) return;
      if (isCleanManualEntitlementBatch(result)) {
        onClose();
        return;
      }
      // Every op has now answered, but the batch had a refusal, or something
      // is still "may be saved": the report stays, with Close only.
      setBatch(result);
      setReport(formatManualEntitlementReport(result));
      setPhase('final');
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof ManualEntitlementSaveError) {
        setBatch(err.batch);
        setReport(err.message);
        setPhase(err.pending.length > 0 ? 'pending' : 'final');
      } else {
        // Not the runner's own failure, so what was sent is unknown: never
        // offer a Retry on a guess.
        setReport(
          err instanceof Error ? err.message : 'Failed to update entitlements'
        );
        setPhase('final');
      }
    } finally {
      if (!signal?.aborted) setProgress(null);
    }
  };

  const handleSave = () => {
    if (phase !== 'editing') return;
    // The diff is taken once, here, before anything is awaited.
    const ops = diffManualEntitlements(baseline, selection);
    if (ops.length === 0) return;
    savedReason.current = reason.trim();
    void run(ops, undefined);
  };

  const handleRetry = () => {
    if (phase !== 'pending' || !batch) return;
    void run([...batch.pending], batch);
  };

  // Ops that still carry "may be saved". Leaving now drops the only repair
  // the console has, so leaving asks first.
  const unconfirmed = batch?.maySaved ?? [];
  const unconfirmedIsPending = unconfirmed.some(f =>
    (batch?.pending ?? []).some(
      p => p.kind === f.op.kind && p.entitlementId === f.op.entitlementId
    )
  );

  // Every way out except the confirmation's own button. While the
  // confirmation shows it does nothing: a second click on the X (or on
  // Discard) must not be read as "yes, discard".
  const handleDismiss = () => {
    if (phase === 'saving' || confirmingDiscard) return;
    if (phase === 'editing') {
      onClose();
      return;
    }
    if (unconfirmed.length > 0) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  };

  // "Discard anyway" / "Close anyway": the only thing that leaves while an op
  // may be saved without its access update.
  const handleConfirmDismiss = () => {
    if (!confirmingDiscard) return;
    onClose();
  };

  const saveLabel =
    progress && progress.total > 1
      ? `Saving ${progress.current} of ${progress.total}…`
      : 'Saving…';

  const retryLabel = batch?.lastAttemptRefused
    ? 'Continue with remaining'
    : 'Retry remaining';

  const leaveVerb = phase === 'pending' ? 'Discard' : 'Close';
  const confirmText = `${unconfirmed.map(f => formatOp(f.op)).join(', ')} may be saved without ${
    unconfirmed.length === 1 ? 'its' : 'their'
  } access update.${unconfirmedIsPending ? ' Retrying completes it.' : ''} ${leaveVerb} anyway?`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop: dismisses only before a batch has started */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={phase === 'editing' ? onClose : undefined}
        />

        {/* Modal */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="entitlement-editor-title"
          className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3
                id="entitlement-editor-title"
                className="text-lg font-medium text-gray-900 dark:text-gray-100"
              >
                Manage Manual Entitlements
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Grant or revoke manual entitlements for this user
              </p>
            </div>
            <button
              onClick={handleDismiss}
              disabled={isSaving || confirmingDiscard}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* User Info Header */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <UserAvatar
              displayName={user.display_name}
              photoUrls={user.photo_url}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.display_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user.uid}
              </p>
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedCatalogueCount} of {availableEntitlements.length}{' '}
                entitlements selected
              </p>
              {hiddenHeldCount > 0 && (
                <p
                  data-testid="hidden-held-note"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {hiddenHeldCount === 1
                    ? '1 other held grant is not in the catalogue, and is left as it is.'
                    : `${hiddenHeldCount} other held grants are not in the catalogue, and are left as they are.`}
                </p>
              )}
            </div>
            <button
              onClick={handleSelectAll}
              disabled={bulkDisabled}
              className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50"
            >
              {allCatalogueSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Entitlements List */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              Available Entitlements
            </h4>

            {entitlementsLoading ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner size="lg" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                  Loading entitlements...
                </span>
              </div>
            ) : isEntitlementsError ? (
              <div className="p-4 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                  Failed to load entitlements
                </p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                  {entitlementsError instanceof Error
                    ? entitlementsError.message
                    : 'Unknown error occurred'}
                </p>
              </div>
            ) : availableEntitlements.length === 0 ? (
              <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No entitlements available
                </p>
              </div>
            ) : isSaving ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="space-y-2">
                {availableEntitlements.map(entitlement => (
                  <EntitlementCard
                    key={entitlement.id}
                    entitlement={entitlement}
                    isSelected={selection.includes(entitlement.id)}
                    onToggle={handleToggleEntitlement}
                    disabled={isLocked}
                    isSubscriptionBundled={subscriptionEntitlementIds.has(
                      entitlement.id
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Reason for Change */}
          <div className="mb-6">
            <label
              htmlFor="entitlement-reason"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2"
            >
              Reason for Change <span className="text-red-500">*</span>
            </label>
            <textarea
              id="entitlement-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Explain why you are changing this user's entitlements..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-sky-500 focus:border-sky-500"
              disabled={isLocked}
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This reason will be logged for audit trail purposes
            </p>
          </div>

          {/* The batch report. Not dismissable: it is the only record of what
              was, and was not, sent. */}
          {report && (
            <div
              role="alert"
              data-testid="save-report"
              className="mb-6 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-200"
            >
              {report}
            </div>
          )}

          {confirmingDiscard && (
            <div
              ref={confirmPanelRef}
              role="alertdialog"
              aria-label={`Confirm ${leaveVerb.toLowerCase()}`}
              className="mb-6 p-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20"
            >
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                {confirmText}
              </p>
              <div className="flex space-x-3">
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmingDiscard(false)}
                  className="flex-1"
                >
                  Keep open
                </FormButton>
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={handleConfirmDismiss}
                  className="flex-1"
                >
                  {`${leaveVerb} anyway`}
                </FormButton>
              </div>
            </div>
          )}

          {/* Action Buttons. Ordinary Save exists only before a batch starts. */}
          <div className="flex space-x-3">
            {phase === 'final' ? (
              <FormButton
                type="button"
                variant="outline"
                onClick={handleDismiss}
                disabled={confirmingDiscard}
                className="flex-1"
              >
                Close
              </FormButton>
            ) : phase === 'pending' ? (
              <>
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleDismiss}
                  disabled={confirmingDiscard}
                  className="flex-1"
                >
                  Discard remaining
                </FormButton>
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={handleRetry}
                  disabled={confirmingDiscard}
                  className="flex-1"
                >
                  {retryLabel}
                </FormButton>
              </>
            ) : (
              <>
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleDismiss}
                  disabled={isSaving}
                  className="flex-1"
                >
                  Cancel
                </FormButton>
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasChanges || !isReasonValid || isSaving}
                  isLoading={isSaving}
                  loadingText={saveLabel}
                  className="flex-1"
                >
                  Save Changes
                </FormButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
